import { Button, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import React from "react";
import { Paras } from "../../typography/Paras";
import { Para } from "../../typography/Para";
import {
  AssignmentTurnedInOutlined,
  CheckCircle,
  Error,
  InfoOutlined,
  Launch,
} from "@mui/icons-material";
import { Callout, CalloutVariant } from "../../Callout";
import { WebsiteAuditResultDto } from "../../../../common/service/server/dto/WebsiteAuditDto";
import { RouteHelpers, Routes } from "../../../root/routes";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { DisableableButton } from "../../Disableable";
import { WebsiteAuditRequirement, WebsiteAuditSuggestion } from "./WebsiteAudit";
import { SurveyAnswers } from "../../../../common/hooks/api/useSurvey";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import {
  CompletedButtonPlaceholder,
  MarkAsCompletedButton,
} from "../../../pages/stay-compliant/PrivacyNoticeChecklistItems";

const isScanSuccessful = (scan: WebsiteAuditResultDto): boolean =>
  [
    "privacyPolicyLinkUrlValid",
    "privacyPolicyLinkTextValid",
    "optOutLinkTextValid",
    "optOutLinkUrlValid",
    "optOutLinkYourPrivacyChoicesIcon",
    "caPrivacyNoticeLinkUrlValid",
    "caPrivacyNoticeLinkTextValid",
    "polarisJsSetup",
    "polarisJsBeforeGoogleAnalytics",
    "polarisJsNoDefer",
    "polarisJsVersion",
  ].every((key) => isSuccessfulValueForScanKey(scan, key));

const isSuccessfulValueForScanKey = (scan: WebsiteAuditResultDto, key: string): boolean => {
  if (key === "polarisJsVersion") {
    return (scan[key] ?? "") !== "";
  }

  return scan[key] === true || scan[key] === null;
};

export type WebsiteAuditResultsProps = {
  useNewDesign?: boolean;
  org: OrganizationDto;
  url: string;
  scan: WebsiteAuditResultDto;
  complete: boolean;
  completeDisabledTooltip: string;
  isFinished?: boolean;
  restartScan: () => Promise<void>;
  markFinished?: () => void;
  handleFormUpdate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  requirements: WebsiteAuditRequirement[];
  suggestions: WebsiteAuditSuggestion[];
  showFinancialIncentiveDisclaimer?: boolean;
  answers: SurveyAnswers;
  inSurvey: boolean;
};

export const WebsiteAuditResults: React.FC<WebsiteAuditResultsProps> = ({
  useNewDesign = false,
  org,
  url,
  scan,
  restartScan,
  complete,
  completeDisabledTooltip,
  handleFormUpdate,
  requirements,
  suggestions,
  answers,
  showFinancialIncentiveDisclaimer,
  markFinished,
  isFinished,
  inSurvey,
}) => {
  const visibleRequirements = requirements.filter(({ shouldHide }) => scan && !shouldHide(scan));

  const newDesignWrapperDivStyles: React.CSSProperties = {
    marginTop: "24px",
    marginBottom: "24px",
    borderRadius: "10px",
    boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.12)",
    padding: "24px 77px",
    width: "945px",
  } as React.CSSProperties;

  const respondedToAllRequirements = visibleRequirements.every(
    (r) =>
      r.shouldHide(scan) ||
      r.isSuccessful(scan) ||
      (answers[r.questionSlug] && answers[r.questionSlug] !== ""),
  );

  const isAnyRequirementMarkedFixed = visibleRequirements.some(
    (r) => !r.shouldHide(scan) && !r.isSuccessful(scan) && answers[r.questionSlug] === "RETRY",
  );

  const [showRequirementsSection, setShowRequirementsSection] = React.useState(true);

  return (
    <div>
      {!useNewDesign && (
        <Paras>
          <Para color="primary" className="d-flex align-items-center mb-mdlg">
            <AssignmentTurnedInOutlined color="primary" className="mr-xs" />
            <strong>Scan of {url} successfully completed</strong>
          </Para>
        </Paras>
      )}
      {useNewDesign && !showRequirementsSection && (
        <div className="mb-md">
          <CompletedButtonPlaceholder />
        </div>
      )}

      {useNewDesign ? (
        !isScanSuccessful(scan) && showRequirementsSection ? (
          <FoundIssuesCalloutNewDesign url={url} />
        ) : undefined
      ) : (
        <StatusCallout success={isScanSuccessful(scan)} />
      )}

      {showRequirementsSection && (
        <div
          style={
            useNewDesign
              ? newDesignWrapperDivStyles
              : { marginLeft: "64px", marginTop: "64px", marginBottom: "64px" }
          }
        >
          {visibleRequirements.map(
            ({
              questionSlug,
              isSuccessful,
              successText,
              errorText,
              disallowRetry,
              disallowIgnore,
            }) => {
              if (scan && isSuccessful(scan)) {
                return (
                  <div key={questionSlug} className="d-flex align-items-start mb-mdlg">
                    <CheckCircle color="success" />
                    <div className="ml-xs w-100">{successText}</div>
                  </div>
                );
              } else {
                return (
                  <>
                    <div className="d-flex align-items-start mb-mdlg">
                      <Error className="website-audit__error-icon" />
                      <div className="ml-xs w-100">
                        {typeof errorText === "function" ? errorText(scan) : errorText}
                        <p>
                          <a
                            className="website-audit__view-instructions"
                            href={RouteHelpers.instructions.id(org.devInstructionsId)}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            <strong>View Developer Instructions</strong>{" "}
                            <Launch className="website-audit__launch-icon" />
                          </a>
                        </p>
                        <FormControl component="fieldset" className={"mb-md"}>
                          <RadioGroup
                            name={questionSlug}
                            aria-label="Vendor handling response"
                            onChange={handleFormUpdate}
                            value={answers[questionSlug]}
                          >
                            {!disallowRetry && (
                              <FormControlLabel
                                value="RETRY"
                                control={<Radio />}
                                label={
                                  <span className={"text-t2 pr-xs"}>
                                    This issue has been fixed.
                                  </span>
                                }
                              />
                            )}
                            <FormControlLabel
                              value="CREATE_TASK"
                              control={<Radio />}
                              label={
                                <span className={"text-t2 pr-xs"}>
                                  Create a item on my to-do list for me to complete this later.
                                </span>
                              }
                            />
                            {!disallowIgnore && (
                              <FormControlLabel
                                value="IGNORE"
                                control={<Radio />}
                                label={
                                  <span className={"text-t2 pr-xs"}>
                                    Ignore - this issue was identified in error.
                                  </span>
                                }
                              />
                            )}
                          </RadioGroup>
                        </FormControl>
                        {answers[questionSlug] === "RETRY" && !disallowRetry && !useNewDesign && (
                          <Callout variant={CalloutVariant.Purple}>
                            To retry, click the "Rescan My Site" button below.
                          </Callout>
                        )}
                        {(isFinished || !inSurvey) && answers[questionSlug] === "CREATE_TASK" && (
                          <Callout variant={CalloutVariant.Green}>
                            We’ll create an item on your to-do list for you shortly to address this
                            issue later.
                          </Callout>
                        )}
                      </div>
                    </div>
                  </>
                );
              }
            },
          )}

          {suggestions.map(({ questionSlug, shouldDisplay, text }) => {
            if (scan && shouldDisplay(scan)) {
              return (
                <>
                  <div className="d-flex align-items-start mb-mdlg">
                    <InfoOutlined color="secondary" />
                    <div className="ml-xs w-100">
                      {text}
                      <p>
                        <a
                          className="website-audit__view-instructions"
                          href={RouteHelpers.instructions.id(org.devInstructionsId)}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          <strong>View Developer Instructions</strong>{" "}
                          <Launch className="website-audit__launch-icon" />
                        </a>
                      </p>
                      <FormControl component="fieldset" className={"mb-md"}>
                        <RadioGroup
                          name={questionSlug}
                          aria-label="Vendor handling response"
                          onChange={handleFormUpdate}
                          value={answers[questionSlug]}
                        >
                          <FormControlLabel
                            value="RETRY"
                            control={<Radio />}
                            label={
                              <span className={"text-t2 pr-xs"}>This issue has been fixed.</span>
                            }
                          />
                          <FormControlLabel
                            value="CREATE_TASK"
                            control={<Radio />}
                            label={
                              <span className={"text-t2 pr-xs"}>
                                Create an item on my to-do list for me to complete this later.
                              </span>
                            }
                          />
                          <FormControlLabel
                            value="IGNORE"
                            control={<Radio />}
                            label={
                              <span className={"text-t2 pr-xs"}>
                                Ignore - this issue was identified in error.
                              </span>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                      {answers[questionSlug] === "RETRY" && !useNewDesign && (
                        <Callout variant={CalloutVariant.Purple}>
                          To retry, click the "Rescan My Site" button below.
                        </Callout>
                      )}
                      {(isFinished || !inSurvey) && answers[questionSlug] === "CREATE_TASK" && (
                        <Callout variant={CalloutVariant.Green}>
                          We’ll create an item on your to-do list for you shortly to address this
                          issue later.
                        </Callout>
                      )}
                    </div>
                  </div>
                </>
              );
            }
          })}

          {(!isFinished || !complete) && respondedToAllRequirements && useNewDesign && (
            <>
              <MarkAsCompletedButton
                onClick={async () => {
                  if (isAnyRequirementMarkedFixed) {
                    await restartScan();
                  } else {
                    setShowRequirementsSection(false);
                  }
                }}
                label={isAnyRequirementMarkedFixed ? "Rescan my Site" : undefined}
                width="auto"
              />
            </>
          )}
        </div>
      )}

      {showFinancialIncentiveDisclaimer && (
        <Callout
          variant={useNewDesign ? undefined : CalloutVariant.Yellow}
          icon={InfoOutlined}
          // @ts-ignore style is generic prop on all Material UI components
          style={useNewDesign ? { width: "945px", color: "#F4F7FF" } : undefined}
        >
          <div className={useNewDesign ? "d-flex" : ""}>
            {useNewDesign && (
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ width: "16px", height: "16px", color: "#60A5FA" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
            <div className={useNewDesign ? "ml-xs" : ""}>
              <p style={useNewDesign ? { color: "#006296" } : undefined}>
                Please note: If you’re using a pop-up for financial incentives, the website audit
                will not be able to scan and find your notice of financial incentives. Check that
                your Notice of Financial Incentive appears like the example image below.
              </p>
              {useNewDesign && <p style={{ color: "#007BBD" }}>For example:</p>}
            </div>
          </div>
          <p className="text-center">
            <img src="/assets/images/surveys/2023q3/financial-incentive-example.jpg" width="60%" />
          </p>
        </Callout>
      )}

      {(!isFinished || !complete) && !useNewDesign && (
        <div className="my-xl d-flex justify-end">
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            onClick={restartScan}
          >
            Rescan my Site
          </Button>
          {markFinished && inSurvey && !isFinished && (
            <DisableableButton
              disabled={!complete}
              disabledTooltip={completeDisabledTooltip}
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              className="px-lg ml-mdlg"
              onClick={markFinished}
            >
              Done
            </DisableableButton>
          )}
          {!inSurvey && (
            <DisableableButton
              disabled={!complete}
              disabledTooltip={completeDisabledTooltip}
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              className="px-lg ml-mdlg"
              href={Routes.tasks.open}
            >
              Go To To-do List
            </DisableableButton>
          )}
        </div>
      )}
    </div>
  );
};

const StatusCallout: React.FC<{ success: boolean }> = ({ success }) => {
  if (success) {
    return (
      <Callout
        variant={CalloutVariant.Green}
        title="Everything looks good! No issues found."
        className="mb-mdlg"
      />
    );
  }
  return (
    <Callout variant={CalloutVariant.Yellow} title="We've found some issues." className="mb-mdlg">
      Our search indicates you are missing certain requirements from our setup instructions. Please
      note that our website scan may not be 100% accurate. If you feel a result is in error, click
      “Ignore.” Otherwise, you may address the issue now, or create an item on your to-do list to do
      so later.
    </Callout>
  );
};

const FoundIssuesCalloutNewDesign = ({ url }: { url: string }) => {
  return (
    <div
      style={{
        backgroundColor: "#FFFBE4",
        borderRadius: "8px",
        boxShadow: "0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
        padding: "19px",
        width: "945px",
      }}
    >
      <div style={{ display: "flex" }}>
        <div>
          <ExclamationTriangleIcon style={{ color: "#FBBF24", height: "20px", width: "20px" }} />
        </div>
        <div style={{ marginLeft: "12px" }}>
          <div style={{ color: "#714219", fontWeight: 500, fontSize: "16px" }}>
            Some issues were found while scanning {url}
          </div>
          <div style={{ color: "#A47028", fontWeight: 400, fontSize: "16px", marginTop: "12px" }}>
            Our search indicates you are missing certain requirements from our setup instructions.
            Please note that our website scan may not be 100% accurate. If you feel a result is in
            error, click “Ignore.” Otherwise, you may address the issue now, or create an item on
            your to-do list to do so later.
          </div>
        </div>
      </div>
    </div>
  );
};
